/**
 * src/components/shared/BreadcrumbCard.jsx — Breadcrumb Card (Voice v2)
 * AC Nautitech — Voice Breadcrumbs
 *
 * Shows voice note with headline, body summary, and scrollable action items.
 * Assignee is a dropdown of active team members. Actions create tasks/bundles
 * depending on context.
 *
 * Sub-components extracted to ./breadcrumb/:
 *   BreadcrumbVoice.jsx  — transcript display, audio playback
 *   BreadcrumbActions.jsx — action list (pending/accepted/dismissed with forms)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Mic, ChevronDown, ChevronUp, Loader2, AlertCircle,
  Archive, CheckCircle2, Trash2,
} from 'lucide-react';
import { updateTranscript } from '../../lib/api/voice';
import { updateAction, processVoice, deleteVoiceNote, archiveVoiceNote } from '../../lib/api/voice-v2';
import { useAuth } from '../../auth/AuthContext';
import { useT } from '../../lib/i18n/I18nContext';
import { useContentLanguage } from '../../lib/i18n/useContentLanguage';
import { useLocalizedRows } from '../../lib/i18n/useLocalizedRows';
import { useProductCategories } from '../../lib/lookups/LookupContext';
import { FALLBACK_PRODUCT_CATEGORIES } from '../../lib/constants/categories';
import { useAppDataContext } from '../../lib/AppDataContext';
import { useToast } from './ToastProvider';
import ContentLangToggle from './ContentLangToggle';
import { BreadcrumbTranscript, BreadcrumbAudioPlayer } from './breadcrumb/BreadcrumbVoice';
import BreadcrumbActions from './breadcrumb/BreadcrumbActions';
import BreadcrumbClassWarnings from './breadcrumb/BreadcrumbClassWarnings';
import { suggestBundleForAction } from '../../lib/voice/suggestBundle';

// ── Helpers ──────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Scope summary helper ────────────────────────────────────────────

/**
 * Build a "Scope / Description" pre-fill for a bundle / task accept form.
 *
 * CEO 2026-04-26 (B1): "Scope description should be a summary of bullets,
 * not the raw transcript paragraph." Earlier this helper appended the
 * voice note's body as a fallback — that polluted derived_actions on every
 * Convert click and showed a verbose paragraph in the form. The body-append
 * is gone. Bullet-quality scope summaries come from classifier detail_json
 * (B4 will harden the classifier prompt to always emit them).
 *
 * Preference order:
 *   1. action.detail_json.steps / bullet_points / bullets / summary_bullets
 *   2. action.description (only if it adds info beyond the title)
 *   3. action.description as final fallback (even if it echoes title)
 *
 * NEVER falls back to bc.body / bc.summary — that's the regression we fixed.
 * Returns '' when nothing useful exists; the user fills in the scope.
 *
 * The `bc` parameter is preserved for caller compatibility but unused.
 * Exported for unit-testing.
 */
// eslint-disable-next-line no-unused-vars -- bc kept for caller signature stability
export function buildScopeSummary(action = {}, bc = {}) {
  const title = String(action.title || '').trim();
  const dj = action.detail_json || {};

  const bullets = Array.isArray(dj.steps) ? dj.steps
    : Array.isArray(dj.bullet_points) ? dj.bullet_points
    : Array.isArray(dj.bullets) ? dj.bullets
    : Array.isArray(dj.summary_bullets) ? dj.summary_bullets
    : null;

  const lines = [];
  if (bullets && bullets.length > 0) {
    for (const b of bullets) {
      const txt = String(b || '').trim();
      if (txt) lines.push(`• ${txt}`);
    }
  }

  const desc = String(action.description || '').trim();
  // Skip the echo-of-title case (one-word breadcrumbs where title === desc).
  if (desc && desc.toLowerCase() !== title.toLowerCase()) {
    lines.push(desc);
  }

  // Final fallback: pure description (even if it echoes title) so the field
  // is at least seeded with the AI's best guess rather than empty.
  if (lines.length === 0 && desc) lines.push(desc);

  return lines.join('\n').trim();
}

// ── People mapping helper ───────────────────────────────────────────

function mapPeopleFromContext(rawPeople) {
  return (rawPeople || [])
    .filter(r => r.archived_at == null)
    .map(r => ({ id: r.id, name: r.display_name || 'Unknown', role: r.role || '', email: r.email || '' }));
}

/** TICKET-1471: the stored (source-language) action behind a card that may show a translated copy. */
function sourceAction(bc, shown) {
  return (bc?.actions || []).find((a) => a.id === shown?.id) || shown;
}

// ── Component ────────────────────────────────────────────────────────

export default function BreadcrumbCard({ breadcrumb, onActionUpdate, onRefresh, onNavigateToEntity }) {
  const { t } = useT();
  const { user } = useAuth();
  const { lang } = useContentLanguage();
  const { showError, showSuccess, showInfo } = useToast();
  const { people: rawPeople, projects = [], bundles = [], departments = [], handleCreateProject, silentReload } = useAppDataContext();
  const dbCategories = useProductCategories();

  // Localize voice note content (headline/summary/body) to the viewer's chosen language
  const { rows: localizedBreadcrumbs, translating, error: translateError } = useLocalizedRows(
    'voice_records',
    breadcrumb ? [breadcrumb] : [],
    lang,
    { reload: onRefresh }
  );
  const localizedBreadcrumb = localizedBreadcrumbs[0] || breadcrumb || {};
  // TICKET-1471: the action cards follow the same Original/EN/ES switch (TICKET-959 path).
  // Display only — Convert / Edit read the SOURCE row (sourceAction), never the shown copy.
  const { rows: localizedActions } = useLocalizedRows('derived_actions', breadcrumb?.actions || [], lang, { reload: onRefresh });
  const CATEGORIES = dbCategories.length > 0
    ? dbCategories.map(c => c.label_en || c.slug)
    : FALLBACK_PRODUCT_CATEGORIES;
  // TICKET-852 (CEO 2026-09-16, recurrence of 2026-04-21 Trace 19b): converting a
  // voice action into a bundle created the row server-side, but the caller's
  // onRefresh only reloaded the VOICE breadcrumbs. ProjectWorkspace patched this
  // locally by also calling silentReload(); the deal side (DealHistoryTab) and
  // TaskNotesSection never did, so a bundle created from a deal's voice note was
  // invisible on the Bundles tab until a full app reload. Refresh app data here,
  // where every caller routes through, instead of per-call-site.
  const refreshAfterCreate = useCallback(() => {
    onRefresh?.();
    silentReload?.();
  }, [onRefresh, silentReload]);

  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [dismissedIds, setDismissedIds] = useState(new Set());
  // Locally-accepted actions (optimistic Convert). Mirrors dismissedIds so a
  // converted item leaves the pending list INSTANTLY without waiting on the
  // parent's onRefresh — CEO 2026-06-18: converting ~17 Silvana items must not
  // trigger a whole-page reload per click. Server data (status='accepted')
  // supersedes this set on the next silent refresh.
  const [acceptedIds, setAcceptedIds] = useState(new Set());
  const [pendingDismissIds, setPendingDismissIds] = useState([]); // visible undo bars
  const pendingTimers = useRef({}); // { actionId: timeoutId } — stable ref, no re-render
  const [retrying, setRetrying] = useState(false);
  const people = mapPeopleFromContext(rawPeople);
  const [currentUserName, setCurrentUserName] = useState('');
  const [entityLinks, setEntityLinks] = useState({}); // actionId → { type, id }

  // Resolve current user name from context people
  useEffect(() => {
    if (!people.length) return;
    const email = user?.email || '';
    const match = people.find(p => p.email === email)
      || people.find(p => p.name && email.includes(p.name.toLowerCase().split(' ')[0]));
    setCurrentUserName(match?.name || people[0]?.name || '');
  }, [people, user]);

  const bc = breadcrumb || {};
  const status = bc.status || 'uploaded';
  const isProcessing = status === 'transcribing' || status === 'classifying';
  // 2026-04-20 Phase 3 — the voice pipeline sets 'transcription_failed'
  // (lib/handlers/voice-v2.js) when Whisper + Gemini both miss a chunk;
  // treat it as a recoverable error state so the existing Retry button
  // renders + handleRetry -> processVoice(bc.id) kicks off a reprocess
  // without requiring the user to re-record. Preserves the voice that
  // was already captured — CEO: "important not to lose the voice that
  // is recorded."
  const isError = status === 'error' || status === 'transcription_failed';

  // Headline + body from classification (new fields) — localized to viewer's language
  const headline = localizedBreadcrumb.headline || '';
  const body = localizedBreadcrumb.body || '';
  const summary = localizedBreadcrumb.summary || '';

  // Latest transcript (never localized — raw transcripts stay as recorded)
  const transcripts = bc.transcripts || [];
  const latestTx = transcripts[0] || {};
  const transcriptText = latestTx.edited_transcript || latestTx.raw_transcript || '';

  // Classification
  const classifications = bc.classifications || [];
  const latestClass = classifications[0] || {};
  const classJson = latestClass.classification_json || bc.latest_classification || {};

  // Use classification headline/body if record-level fields are empty — localized
  const displayHeadline = headline || classJson.headline || '';
  const displayBody = body || classJson.body || summary || classJson.summary || '';

  // Actions — separate by status for state machine rendering
  const allActions = localizedActions;
  const actionsWithTitle = allActions.filter(a => a.title);

  // 7-day window for dismissed actions visibility
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const sevenDaysAgo = Date.now() - SEVEN_DAYS_MS;

  // Dismissed actions: from DB (status=dismissed) + locally dismissed
  const dismissedActions = actionsWithTitle.filter(a => {
    if (a.status !== 'dismissed' && !dismissedIds.has(a.id)) return false;
    const ts = a.updated_at ? new Date(a.updated_at).getTime() : (a.created_at ? new Date(a.created_at).getTime() : Date.now());
    return ts >= sevenDaysAgo;
  });

  // Accepted actions (from DB) + locally-accepted (optimistic Convert)
  const acceptedActions = actionsWithTitle.filter(a => a.status === 'accepted' || acceptedIds.has(a.id));

  // Pending actions (not accepted, not dismissed, not superseded by a
  // reprocess, not in local pending dismiss). CEO 2026-04-26: when the user
  // edits a transcript and clicks "Save & reprocess actions", the server
  // marks the prior batch 'superseded' and inserts a fresh batch — the
  // stale ones must NOT keep rendering as pending alongside the new ones.
  const pendingActions = actionsWithTitle.filter(a =>
    a.status !== 'accepted' && a.status !== 'dismissed' && a.status !== 'superseded'
    && !dismissedIds.has(a.id) && !pendingDismissIds.includes(a.id)
    && !acceptedIds.has(a.id)
  );

  // Undo-bar actions (locally pending dismiss but not yet persisted)
  const undoBarActions = actionsWithTitle.filter(a => pendingDismissIds.includes(a.id) && a.status !== 'dismissed');

  // Summary counts for header
  const acceptedCount = acceptedActions.length;
  const pendingCount = pendingActions.length + undoBarActions.length;
  const dismissedCount = dismissedActions.length;
  // All actions resolved (accepted or dismissed) — show archive prompt
  const isResolved = actionsWithTitle.length > 0 && actionsWithTitle.every(a =>
    a.status === 'accepted' || a.status === 'dismissed' || dismissedIds.has(a.id) || acceptedIds.has(a.id)
  );

  // Auto-collapse breadcrumb when all actions resolved
  useEffect(() => {
    if (isResolved && expanded) {
      const timer = setTimeout(() => setExpanded(false), 800);
      return () => clearTimeout(timer);
    }
  }, [isResolved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Audio URL
  const audioUrl = bc.audio_url || bc.storage_path || null;

  // Preview text for compact view
  const previewText = displayHeadline
    || (transcriptText ? (transcriptText.length > 80 ? transcriptText.slice(0, 80) + '...' : transcriptText) : '')
    || (isError ? 'Error processing voice note' : (isProcessing ? 'Processing...' : 'No transcript'));

  // ── Handlers ─────────────────────────────────────────────────────

  const startEdit = useCallback(() => { setEditText(transcriptText); setEditing(true); }, [transcriptText]);
  const cancelEdit = useCallback(() => { setEditing(false); setEditText(''); }, []);

  const saveEdit = useCallback(async (reprocess = false) => {
    if (!bc.id) return;
    setSaving(true);
    try {
      await updateTranscript(bc.id, editText, { reprocess });
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('[BreadcrumbCard] save error:', err);
    } finally { setSaving(false); }
  }, [bc.id, editText, onRefresh]);

  const openForm = useCallback((shown) => {
    const action = sourceAction(bc, shown); // TICKET-1471: the form edits the source text
    setActiveForm(action.id);
    // Seed project from AI hint or voice-record scope so the user sees a
    // pre-selected best guess rather than an empty dropdown.
    const seededProjectId =
      action.user_edits?.project_id
      ?? action.detail_json?.project_id
      ?? bc.project_id
      ?? '';
    const seededDepartmentId =
      action.user_edits?.department_id
      ?? action.detail_json?.department_id
      ?? '';
    // Pre-fill the bundle from the AI suggestion so Edit & Accept opens
    // consistent with the inline quick-convert picker (CEO 2026-06-18).
    const bundlesForSeed = (bundles || []).filter(
      b => (b.project_fk === seededProjectId || b.project_id === seededProjectId) && !b.archived_at,
    );
    const seededBundleId =
      action.user_edits?.bundle_id
      ?? suggestBundleForAction(action, bundlesForSeed)?.id
      ?? '';
    setFormData({
      title: action.title || '',
      description: buildScopeSummary(action, bc),
      assignee: action.assignee_hint || currentUserName,
      priority: action.priority_hint || 'P2',
      due_date: action.due_hint || '',
      estimated_value: action.detail_json?.estimated_value || '',
      product_category: action.detail_json?.product_category || '',
      project_id: seededProjectId,
      department_id: seededDepartmentId,
      bundle_id: seededBundleId,
    });
  }, [currentUserName, bc, bundles]);

  const cancelForm = useCallback(() => { setActiveForm(null); setFormData({}); }, []);

  // Quick-convert: one-click accept using action hints as-is (no form)
  const [convertingId, setConvertingId] = useState(null);
  // TICKET-1369 (P1, peer review 2026-10-19 #15): apiFetch RETURNS {success:false} on a
  // 4xx/5xx or a timeout. Only `creation_error` used to count as a failure, so a failed
  // accept said "Created", cleared the form and hid the action. An explicit
  // success:false is a failure; an undefined result (parent handlers) is not.
  const failedResult = (r) => r?.success === false;
  // Ref, not state: two taps in the same tick both read the old convertingId.
  const convertingRef = useRef(null);

  const quickConvert = useCallback(async (shown, bundleId) => {
    if (convertingRef.current) return; // in flight -- one request per tap burst
    const action = sourceAction(bc, shown); // TICKET-1471: the task gets the source text
    convertingRef.current = action.id;
    setConvertingId(action.id);
    try {
      const edits = {
        title: action.title || '',
        description: buildScopeSummary(action, bc),
        assignee: action.assignee_hint || currentUserName,
        priority: action.priority_hint || 'P2',
        due_date: action.due_hint || '',
      };
      // CEO 2026-06-18: one-click Convert now carries the chosen/suggested
      // bundle so transcript tasks land in the right bundle without opening
      // the full form. Empty string clears any bundle; server applies it
      // (inherits the bundle stage, guards project mismatch).
      if (bundleId !== undefined) edits.bundle_id = bundleId || null;
      const payload = {
        actionId: action.id,
        operation: 'accept',
        edits,
      };
      let result;
      if (onActionUpdate) result = await onActionUpdate(payload);
      else result = await updateAction(payload);
      if (result?.creation_error || failedResult(result)) {
        // CEO 2026-04-21/26: silent console.warn hid the failure and the
        // form/orb appeared frozen. Surface via toast so user can retry.
        const why = result.creation_error || result.error || 'not saved';
        console.warn('[BreadcrumbCard] quick convert failed:', why);
        showError(`Convert failed: ${why}`);
      } else {
        if (result?.linked_task_id) setEntityLinks(prev => ({ ...prev, [action.id]: { type: 'task', id: result.linked_task_id } }));
        if (result?.linked_bundle_id) setEntityLinks(prev => ({ ...prev, [action.id]: { type: 'bundle', id: result.linked_bundle_id } }));
        if (result?.linked_quote_id) setEntityLinks(prev => ({ ...prev, [action.id]: { type: 'quote', id: result.linked_quote_id } }));
        if (result?.note) showInfo(result.note);
        // Optimistic: clear the item from the pending list NOW. The parent's
        // onRefresh runs a silent background merge to surface the new task/bundle
        // in other tabs - no whole-page reload (CEO 2026-06-18).
        setAcceptedIds(prev => new Set([...prev, action.id]));
        refreshAfterCreate();
      }
    } catch (err) {
      console.error('[BreadcrumbCard] quick convert error:', err);
      showError(`Convert failed: ${err?.message || 'Unknown error'}`);
    } finally {
      convertingRef.current = null;
      setConvertingId(null);
    }
  }, [currentUserName, bc, onActionUpdate, refreshAfterCreate, showError, showInfo]);

  const [confirmingId, setConfirmingId] = useState(null);
  const confirmAction = useCallback(async (actionId) => {
    if (confirmingId === actionId) return; // prevent double-submit
    setConfirmingId(actionId);
    try {
      const payload = { actionId, operation: 'accept', edits: { ...formData } };
      let result;
      if (onActionUpdate) result = await onActionUpdate(payload);
      else result = await updateAction(payload);
      if (failedResult(result)) {
        // TICKET-1369: nothing was created. Say so and keep the form + what was typed.
        showError(`Create failed: ${result.error || 'not saved, try again'}`);
        return;
      }
      if (result?.creation_error) {
        // CEO 2026-04-26: "Create bundle does not react when I click it."
        // Root cause was this branch silently returning. The server may have
        // succeeded partially (e.g. bundle→task fallback) but the user got
        // no feedback and the form appeared frozen. Now: visible toast,
        // close the form, and let the refresh land any partial state.
        console.warn('[BreadcrumbCard] entity creation failed:', result.creation_error);
        showError(`Create failed: ${result.creation_error}`);
        if (result?.note) showInfo(result.note);
        setActiveForm(null);
        setFormData({});
        refreshAfterCreate();
        return;
      }
      if (result?.linked_task_id) {
        setEntityLinks(prev => ({ ...prev, [actionId]: { type: 'task', id: result.linked_task_id } }));
      }
      if (result?.linked_bundle_id) {
        setEntityLinks(prev => ({ ...prev, [actionId]: { type: 'bundle', id: result.linked_bundle_id } }));
      }
      if (result?.linked_quote_id) {
        setEntityLinks(prev => ({ ...prev, [actionId]: { type: 'quote', id: result.linked_quote_id } }));
      }
      if (result?.note) showInfo(result.note);
      else showSuccess('Created');
      setActiveForm(null);
      setFormData({});
      // Optimistic clear (mirrors quickConvert) - item leaves the pending list
      // immediately; onRefresh syncs other tabs silently (CEO 2026-06-18).
      setAcceptedIds(prev => new Set([...prev, actionId]));
      refreshAfterCreate();
    } catch (err) {
      console.error('[BreadcrumbCard] confirm error:', err);
      showError(`Create failed: ${err?.message || 'Network error'}`);
    } finally {
      setConfirmingId(null);
    }
  }, [confirmingId, formData, onActionUpdate, refreshAfterCreate, showError, showSuccess, showInfo]);

  // CEO 2026-04-26: type-override loop. Tap the type pill on a misclassified
  // action and switch task↔bundle_item↔follow_up↔note in one click — saves a
  // full reprocess (and the Gemini call that goes with it).
  const overrideType = useCallback(async (actionId, newType) => {
    try {
      const payload = { actionId, operation: 'edit', edits: { action_type: newType } };
      const result = onActionUpdate ? await onActionUpdate(payload) : await updateAction(payload);
      if (result?.error) {
        showError(`Type change failed: ${result.error}`);
        return;
      }
      showSuccess('Type updated');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('[BreadcrumbCard] override type error:', err);
      showError(`Type change failed: ${err?.message || 'Network error'}`);
    }
  }, [onActionUpdate, onRefresh, showError, showSuccess]);

  const dismissAction = useCallback((actionId) => {
    if (pendingTimers.current[actionId]) clearTimeout(pendingTimers.current[actionId]);
    setPendingDismissIds(prev => prev.includes(actionId) ? prev : [...prev, actionId]);
    pendingTimers.current[actionId] = setTimeout(async () => {
      delete pendingTimers.current[actionId];
      setDismissedIds(prev => new Set([...prev, actionId]));
      setPendingDismissIds(prev => prev.filter(id => id !== actionId));
      try {
        const payload = { actionId, operation: 'dismiss' };
        if (onActionUpdate) await onActionUpdate(payload);
        else await updateAction(payload);
      } catch (err) { console.error('[BreadcrumbCard] dismiss error:', err); }
    }, 5000);
  }, [onActionUpdate]);

  const undoDismiss = useCallback((actionId) => {
    if (pendingTimers.current[actionId]) {
      clearTimeout(pendingTimers.current[actionId]);
      delete pendingTimers.current[actionId];
    }
    setPendingDismissIds(prev => prev.filter(id => id !== actionId));
  }, []);

  // Cleanup all timers on unmount only
  useEffect(() => {
    const timers = pendingTimers.current;
    return () => { Object.values(timers).forEach(t => clearTimeout(t)); };
  }, []);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = useCallback(async () => {
    if (!bc.id || deleting) return;
    setDeleting(true);
    try {
      await deleteVoiceNote(bc.id);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('[BreadcrumbCard] delete error:', err);
      setDeleting(false);
    }
  }, [bc.id, deleting, onRefresh]);

  const [archiving, setArchiving] = useState(false);
  const handleArchive = useCallback(async () => {
    if (!bc.id || archiving) return;
    setArchiving(true);
    try {
      await archiveVoiceNote(bc.id);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('[BreadcrumbCard] archive error:', err);
      setArchiving(false);
    }
  }, [bc.id, archiving, onRefresh]);

  const handleRetry = useCallback(async () => {
    if (!bc.id || retrying) return;
    setRetrying(true);
    try {
      const result = await processVoice(bc.id);
      if (result?.status === 'ready' || result?.success) { if (onRefresh) onRefresh(); }
    } catch (err) { console.error('[BreadcrumbCard] retry error:', err); }
    finally { setRetrying(false); }
  }, [bc.id, retrying, onRefresh]);

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div data-testid="breadcrumb-card" data-breadcrumb-id={bc.id}
      className="rounded-xl md:rounded-2xl overflow-hidden transition-all duration-200"
      style={{ background: 'var(--card)', border: '1px solid var(--bdr)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', opacity: isResolved ? 0.65 : 1 }}>

      {/* ── Compact Row ── */}
      <div onClick={() => setExpanded(prev => !prev)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setExpanded(prev => !prev))(e); } }}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 20px', cursor: 'pointer' }}>
        <Mic size={14} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1, #1B2B4B)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {previewText}
          </div>
          {displayBody && !expanded && (
            <div style={{ fontSize: 11, color: 'var(--tm, #8896AB)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayBody.length > 80 ? displayBody.slice(0, 80) + '...' : displayBody}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 4 }}>
          {isProcessing && <Loader2 size={12} color="var(--accent)" className="animate-spin" />}
          {isError && <AlertCircle size={12} color="var(--bad, #EF4444)" />}
          {isResolved && <CheckCircle2 size={12} color="var(--ok)" />}
          {actionsWithTitle.length > 0 && (
            <span style={{ fontSize: 10, color: 'var(--tm)', whiteSpace: 'nowrap' }}>
              {acceptedCount > 0 && <span style={{ color: 'var(--ok, #22C55E)' }}>{acceptedCount} done</span>}
              {acceptedCount > 0 && pendingCount > 0 && ' \u00b7 '}
              {pendingCount > 0 && <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{pendingCount} pending</span>}
              {(acceptedCount > 0 || pendingCount > 0) && dismissedCount > 0 && ' \u00b7 '}
              {dismissedCount > 0 && <span style={{ color: 'var(--tm)' }}>{dismissedCount} dismissed</span>}
            </span>
          )}
          <span className="text-[10px] text-tm whitespace-nowrap">
            {relativeTime(bc.created_at)}
          </span>
          {expanded ? <ChevronUp size={12} color="var(--tm)" /> : <ChevronDown size={12} color="var(--tm)" />}
        </div>
      </div>

      {/* ── Expanded Area ── */}
      {expanded && (
        <div style={{ margin: '0 20px 20px', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 20, borderTop: '1px solid rgba(0,0,0,0.08)' }}>

          {/* Content Language Toggle — TICKET-959 B2 */}
          <ContentLangToggle translating={translating} error={translateError} testIdPrefix="breadcrumb-lang" />

          {/* Processing / Error banners */}
          {isProcessing && (
            // HEX_TODO[#4285F4] exception — see color-map.json
            <div className="flex items-center gap-2 rounded-lg text-[12px] font-medium" style={{ padding: '12px 16px', background: 'rgba(66,133,244,0.08)', color: '#4285F4' }}>
              <Loader2 size={14} className="animate-spin" />
              {status === 'transcribing' ? 'Transcribing audio...' : 'Classifying transcript...'}
            </div>
          )}
          {isError && (
            <div className="flex items-center gap-2 rounded-lg text-[12px] font-medium" style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.08)', color: 'var(--bad)' }}>
              {retrying ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
              <span className="flex-1">{retrying ? 'Retrying...' : 'Failed to process.'}</span>
              {!retrying && (
                <button onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                  className="px-4 rounded-lg text-[12px] font-bold border-none cursor-pointer text-white" style={{ background: 'var(--navy-surface)', minHeight: 44, padding: '8px 16px' }}>
                  Retry
                </button>
              )}
            </div>
          )}

          {/* ── Summary / Body ── */}
          {displayBody && (
            <div style={{ borderLeft: '3px solid var(--accent, #1C3F75)', paddingLeft: 16, paddingRight: 4, paddingTop: 4, paddingBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tm, #8896AB)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Summary
              </div>
              <div style={{ fontSize: 13, color: 'var(--t1, #1B2B4B)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {displayBody}
              </div>
            </div>
          )}

          {/* ── Transcript (collapsible) ── */}
          <BreadcrumbTranscript
            transcriptText={transcriptText}
            editing={editing}
            editText={editText}
            setEditText={setEditText}
            startEdit={startEdit}
            cancelEdit={cancelEdit}
            saveEdit={saveEdit}
            saving={saving}
            t={t}
          />

          {/* ── Audio Playback ── */}
          <BreadcrumbAudioPlayer audioUrl={audioUrl} />

          {/* ── Actions List ── (TICKET-1318: classifier self-check warnings first) */}
          <BreadcrumbClassWarnings warnings={classJson._warnings} />
          <BreadcrumbActions
            pendingActions={pendingActions}
            undoBarActions={undoBarActions}
            acceptedActions={acceptedActions}
            dismissedActions={dismissedActions}
            acceptedCount={acceptedCount}
            pendingCount={pendingCount}
            dismissedCount={dismissedCount}
            entityLinks={entityLinks}
            onNavigateToEntity={onNavigateToEntity}
            activeForm={activeForm}
            formData={formData}
            setFormData={setFormData}
            convertingId={convertingId}
            confirmingId={confirmingId}
            quickConvert={quickConvert}
            openForm={openForm}
            cancelForm={cancelForm}
            confirmAction={confirmAction}
            dismissAction={dismissAction}
            undoDismiss={undoDismiss}
            overrideType={overrideType}
            people={people}
            projects={projects}
            bundles={bundles}
            defaultProjectId={bc.project_id}
            departments={departments}
            onCreateProject={handleCreateProject}
            CATEGORIES={CATEGORIES}
            onActionUpdate={onActionUpdate}
            onRefresh={onRefresh}
            updateAction={updateAction}
          />

          {/* No actions message */}
          {!isProcessing && !isError && actionsWithTitle.length === 0 && transcriptText && (
            <div style={{ fontSize: 12, color: 'var(--tm)', fontStyle: 'italic', padding: '4px 0' }}>
              No actionable items extracted from this recording.
            </div>
          )}

          {/* Resolved banner + Archive */}
          {isResolved && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
              <CheckCircle2 size={14} color="var(--ok)" />
              <span className="text-[12px] font-medium text-t1 flex-1">All actions resolved</span>
              <button onClick={(e) => { e.stopPropagation(); handleArchive(); }} disabled={archiving}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', minHeight: 44, borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', color: 'var(--card)', background: 'var(--accent)', opacity: archiving ? 0.6 : 1 }}>
                <Archive size={12} /> {archiving ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          )}

          {/* Delete voice note */}
          <button onClick={(e) => { e.stopPropagation(); handleDelete(); }} disabled={deleting}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', minHeight: 44, borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid rgba(239,68,68,0.15)', background: 'rgba(239,68,68,0.05)', color: 'var(--bad, #EF4444)', alignSelf: 'flex-start', cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.5 : 1 }}>
            <Trash2 size={12} /> {deleting ? 'Deleting...' : 'Delete Note'}
          </button>
        </div>
      )}
    </div>
  );
}
